import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, MapPin } from "lucide-react";
import { fetchEvents } from "@/api/events";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";

const previewText = (text, maxLength = 160) => {
  const value = String(text || "").trim();
  if (!value || value.length <= maxLength) return value;
  return `${value.slice(0, maxLength).trimEnd()}...`;
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const { t, lv, fmtDate } = useLang();
  useEffect(() => { fetchEvents().then(setEvents).catch(() => {}); }, []);

  return (
    <div data-testid="events-page">
      <PageHeader eyebrow={t.events.eyebrow} title={t.events.title} sub={t.events.sub} />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {events.map((e, i) => (
              <article key={e.id} data-testid={`event-item-${i}`} className="card-soft reveal overflow-hidden flex flex-col h-full">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={e.image} alt={lv(e, "title")} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  <div className="absolute top-4 left-4 bg-white/95 px-3 py-1.5 rounded-full text-[11px] font-semibold text-[#7B1818] flex items-center gap-1.5">
                    <Calendar size={12} /> {fmtDate(e.date)}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-2xl text-[#2A2118] leading-snug">{lv(e, "title")}</h3>
                  <p className="mt-1 text-[12.5px] text-[#8a6a3a] flex items-center gap-1">
                    <MapPin size={12} /> {lv(e, "location")}
                  </p>
                  <div className="mt-4 flex-1">
                    {e.englishText && (
                      <p className="text-[14.5px] text-[#5C4D42] leading-relaxed">
                      {previewText(e.englishText)}
                      </p>
                    )}
                    {e.kannadaText && (
                      <p className="mt-3 text-[14.5px] text-[#5C4D42] leading-relaxed">
                      {previewText(e.kannadaText)}
                      </p>
                    )}
                    {!e.englishText && !e.kannadaText && (
                      <p className="text-[14.5px] text-[#5C4D42] leading-relaxed">{lv(e, "description")}</p>
                    )}
                  </div>
                  <Link
                    to={`/events/${e.id}`}
                    state={{ eventTitle: lv(e, "title"), eventDate: e.date }}
                    className="btn-ghost mt-5 self-start"
                  >
                    {t.common.readMore} <ChevronRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {events.length === 0 && (
            <div className="text-center text-[#5C4D42] py-16">{t.events.empty}</div>
          )}
        </div>
      </section>
    </div>
  );
}
